import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../components/common/Modal/Modal';
import Button from '../../components/common/Button/Button';
import ReceivedAmountForm from './ReceivedAmountForm';
import styles from './ReceivedAmountList.module.scss';
import receivedAmountService from '../../services/receivedAmountService';
import { formatDate, formatCurrency, formatDateTimeDMY } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';

const ReceivedAmountList: React.FC = () => {
  const { auth } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [receivedAmounts, setReceivedAmounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }));

  
  const fetchReceivedAmounts = useCallback(async () => {
    setLoading(true);
    try {
      // Pass date as a query param
      const params = new URLSearchParams();
      params.append('date', selectedDate);

      const data = await receivedAmountService.getFiltered(params.toString());
      setReceivedAmounts(Array.isArray(data) ? data : []);
    } catch (error) {
      setReceivedAmounts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      console.log('selectedDate', selectedDate);
      fetchReceivedAmounts();
    }
  }, [selectedDate, fetchReceivedAmounts]);

  const handleAddReceived = async () => {
    setShowModal(false);
    // Refresh the list from the server
    try {
      fetchReceivedAmounts();
    } catch {
      // Optionally handle error
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deletingId == null) return;
    
    try {
      await receivedAmountService.delete(deletingId);
      // Remove the deleted item from local state
      setReceivedAmounts(prev => prev.filter(item => item.id !== deletingId));
      // Optionally show success message
      alert('Received amount deleted successfully');
    } catch (error) {
      // Handle error
      console.error('Failed to delete received amount:', error);
      alert('Failed to delete received amount');
    } finally {
      setShowDeleteModal(false);
      setDeletingId(null);
    }
  };

  const canEditReceivedAmt = (item: any): boolean => {
    // Admins can always edit
    if (auth.user?.role === 'admin') return true;

    // Employees with canManageInvoices can edit any invoice
    if (auth.user?.permissions?.canViewReports) return true;

    // Employees without canManageInvoices:
    // Can only edit their own invoices created today
    const today = new Date().toLocaleDateString('en-CA');
    const dateObj = new Date(item.created_at);
    dateObj.setDate(dateObj.getDate());
    const payment_date = dateObj.toLocaleDateString('en-CA');
    
    return (
      item.user_id === auth.user?.id &&
      formatDate(payment_date) === formatDate(today)
    );
  };

  const canDeleteReceivedAmt = (item: any): boolean => {
    // Admins can always edit
    if (auth.user?.role === 'admin') return true;

    // Employees without canManageInvoices:
    // Can only edit their own invoices created today
    const today = new Date().toLocaleDateString('en-CA');
    const dateObj = new Date(item.created_at);
    dateObj.setDate(dateObj.getDate());
    const payment_date = dateObj.toLocaleDateString('en-CA');
    console.log('payment_date', payment_date);
    console.log('today', today);
    return (
      item.user_id === auth.user?.id &&
      formatDate(payment_date) === formatDate(today)
    );
  };

  const filteredReceivedAmounts = receivedAmounts.filter(item =>
    item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.amount?.toString().includes(searchTerm) ||
    item.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.payment_method?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Received Amounts</h2>
        <div className={styles.headerActions}>
          <div className={styles.searchContainer}>
            <input
              placeholder="Search by customer name, amount, or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          {/* <select
            value={selectedAccountHead}
            onChange={e => setSelectedAccountHead(e.target.value)}
            className={styles.select}
            style={{ minWidth: 160, marginRight: 8 }}
          >
            <option value="">All Account Heads</option>
            {accountHeads.map((head) => (
              <option key={head.id} value={head.id}>{head.name}</option>
            ))}
          </select> */}
          <div className={styles.dateInputWrapper}>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className={styles.dateFilter}
              placeholder="Select date..."
              max={new Date().toLocaleDateString('en-CA')}
            />
            <span className={styles.dateIcon}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
          </div>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            + Add Received
          </Button>
        </div>
      </div>
      
      <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Method</th>
            {/* <th>Account Head</th> */}
            <th>Notes</th>
            <th>Created By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center' }}>Loading...</td>
            </tr>
          ) : filteredReceivedAmounts.length === 0 ? (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center' }}>No received amounts found.</td>
            </tr>
          ) : (
            filteredReceivedAmounts.map((item) => (
              <tr key={item.id}>
                <td>{item.customer_name}</td>
                <td>{formatCurrency(item.amount)}</td>
                <td>{formatDateTimeDMY(item.created_at)}</td>
                <td>{item.payment_method?.toUpperCase() || '-'}</td>
                {/* <td>{item.account_head || item.account_head_name || '-'}</td> */}
                <td>{item.notes || '-'}</td>
                <td>{item.created_by || '-'}</td>
                <td>
                {canEditReceivedAmt(item) && (
                  <button
                    className={styles.actionButton}
                    onClick={() => handleEdit(item)}
                    title="Edit"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  >
                    {/* Edit (pencil) icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                  </button>
                )}
                {canDeleteReceivedAmt(item) && (
                  <button
                    className={styles.actionButton}
                    onClick={() => handleDelete(item.id)}
                    title="Delete"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginLeft: 8 }}
                  >
                    {/* Delete (trash) icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                  </button>
                )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      <Modal 
  isOpen={showModal} 
  onClose={() => { setShowModal(false); setEditingItem(null); }} 
  title={editingItem ? "Edit Received Amount" : "Add Received Amount"} 
  size="lg"
>
        <ReceivedAmountForm
          onSuccess={handleAddReceived}
          editingItem={editingItem}
        />
      </Modal>
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <p>Are you sure you want to delete this received amount?</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ReceivedAmountList; 
